/**
 * GuidedQuestionsStep Component
 *
 * Step 3B: Answer guided questions about the assessment.
 */

import { useEffect, useRef, useState } from 'react';
import { WizardState, WIZARD_LIMITS, GUIDED_QUESTIONS, GuidedAnswers } from './types';
import { apiClient } from '../../lib/api';

interface GuidedQuestionsStepProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
  errors: string[];
  trainingName?: string;
  objective?: string;
}

export function GuidedQuestionsStep({ state, onChange, errors, trainingName, objective }: GuidedQuestionsStepProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!trainingName || trainingName.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const result = await apiClient.suggestCompetencies({
          trainingName: trainingName.trim(),
          objective: objective?.trim(),
        });
        if (result.response.data?.suggestions) {
          setSuggestions(result.response.data.suggestions);
        }
      } catch {
        // Silently fail — suggestions are optional
      } finally {
        setSuggestionsLoading(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [trainingName, objective]);

  const handleAnswerChange = (key: keyof GuidedAnswers, value: string) => {
    onChange({
      guidedAnswers: {
        ...state.guidedAnswers,
        [key]: value,
      },
    });
  };

  const currentQ = GUIDED_QUESTIONS[currentQuestion]!;
  const currentAnswer = currentQ ? (state.guidedAnswers[currentQ.key] || '') : '';
  const answeredCount = GUIDED_QUESTIONS.filter(
    (q) => state.guidedAnswers[q.key]?.trim()
  ).length;

  return (
    <div>
      <h2 style={{ marginBottom: 8 }}>Guided Questions</h2>
      <p className="text-muted" style={{ marginBottom: 24 }}>
        Answer these questions to help AI generate a relevant assessment package.
        You can skip questions, but more context produces better results.
      </p>

      {errors.length > 0 && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {errors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {GUIDED_QUESTIONS.map((q, index) => {
          const isAnswered = !!state.guidedAnswers[q.key]?.trim();
          const isCurrent = index === currentQuestion;

          return (
            <button
              key={q.key}
              type="button"
              onClick={() => setCurrentQuestion(index)}
              title={q.label}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                background: isCurrent
                  ? 'var(--accent-primary)'
                  : isAnswered
                    ? 'var(--semantic-success)'
                    : 'var(--border-default)',
                transition: 'all 0.2s',
              }}
            />
          );
        })}
        <span
          style={{
            marginLeft: 8,
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          {answeredCount} of {GUIDED_QUESTIONS.length} answered
        </span>
      </div>

      {/* AI Competency Suggestions */}
      {currentQuestion === 0 && (suggestions.length > 0 || suggestionsLoading) && (
        <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg-muted)', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            <span>Suggested Competencies</span>
            {suggestionsLoading && <span style={{ fontSize: 11 }}>(loading...)</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map((s) => {
              const selected = currentAnswer.split(',').map(v => v.trim()).filter(Boolean).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    const existing = currentAnswer.split(',').map(v => v.trim()).filter(Boolean);
                    if (selected) {
                      handleAnswerChange(currentQ.key, existing.filter(v => v !== s).join(', '));
                    } else {
                      handleAnswerChange(currentQ.key, [...existing, s].join(', '));
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 9999,
                    border: '1px solid var(--border-default)',
                    background: selected ? 'var(--sky-500)' : 'white',
                    color: selected ? 'white' : 'var(--text-primary)',
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Current question */}
      <div
        style={{
          background: 'var(--bg-muted)',
          borderRadius: 8,
          padding: 24,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontSize: 12,
              padding: '2px 8px',
              background: 'var(--accent-primary)',
              color: '#fff',
              borderRadius: 4,
            }}
          >
            {currentQuestion + 1} of {GUIDED_QUESTIONS.length}
          </span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{currentQ.label}</span>
        </div>

        <p style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 500 }}>
          {currentQ.question}
        </p>

        <textarea
          className="input"
          value={currentAnswer}
          onChange={(e) => handleAnswerChange(currentQ.key, e.target.value)}
          placeholder={currentQ.placeholder}
          rows={4}
          maxLength={WIZARD_LIMITS.GUIDED_ANSWER_MAX}
          style={{ width: '100%', fontFamily: 'inherit' }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {currentAnswer.length} / {WIZARD_LIMITS.GUIDED_ANSWER_MAX}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setCurrentQuestion((prev) => Math.max(0, prev - 1))}
          disabled={currentQuestion === 0}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            setCurrentQuestion((prev) => Math.min(GUIDED_QUESTIONS.length - 1, prev + 1))
          }
          disabled={currentQuestion === GUIDED_QUESTIONS.length - 1}
        >
          Next Question
        </button>
      </div>

      {/* All answers summary */}
      {answeredCount > 0 && (
        <div style={{ marginTop: 24 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>Your Answers Summary</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {GUIDED_QUESTIONS.filter((q) => state.guidedAnswers[q.key]?.trim()).map((q) => (
              <div
                key={q.key}
                style={{
                  padding: '8px 12px',
                  background: 'var(--bg-muted)',
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 500 }}>{q.label}:</span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>
                  {state.guidedAnswers[q.key]!.length > 100
                    ? state.guidedAnswers[q.key]!.slice(0, 100) + '...'
                    : state.guidedAnswers[q.key]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
